import {
  Accordion,
  Card,
  Checkbox,
  CloseButton,
  Flex,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  setDocumentFilterHUDOC,
  setDocumentFilterICJ,
  setDocumentFilterTime,
} from '@/lib/state/slices/ProjectStateSlice';
import {
  Article,
  Conclusion,
  CountryHUDOC,
  CountryICJ,
  Database,
  DecisionLevel,
  FilterDefaultOptions,
  filterOption,
  FilterRenderType,
  FilterSource,
  Importance,
} from '@/lib/state/ViewTypes';
import { useAppDispatch, useAppSelector } from '@/lib/hooks';
import { AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '../ui/accordion';
import DateRangeSelector from './DateRangeSelector';
import StyledButton from '../ui/styledButton';
import React from 'react';
import { HiOutlineMinus } from 'react-icons/hi';

function AccordionCheckboxDropdown(props: {
  label: string;
  isChecked: boolean;
  isPartial: boolean;
  onClick: () => void;
  children?: React.ReactNode;
  bg?: string;
  variant?: 'compact' | 'default';
}) {
  return (
    <Accordion.Item
      value={props.label}
      borderRadius="0.375rem"
      backgroundColor={props.bg ? props.bg : 'midCardBG'}
      style={{ boxShadow: '0px 3px 1px -3px #00000080' }}
    >
      <Flex
        direction="row"
        width="full"
        height="100%"
        boxShadow="none"
        justifyItems="end"
        alignContent="center"
        borderRadius="0.375rem"
        backgroundColor={props.bg ? props.bg : 'midCardBG'}
      >
        <Checkbox.Root
          padding={2}
          height="100%"
          _hover={{
            bg: 'cardHover',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            borderRadius: '0.375rem',
          }}
          checked={props.isChecked}
          onCheckedChange={() => props.onClick()}
        >
          <Checkbox.Control
            height="100%"
            // padding={2}
            borderColor={props.isPartial ? 'secondaryText' : 'primaryText'}
            backgroundColor={
              props.isPartial ? 'secondaryText' : props.isChecked ? 'primaryText' : 'none'
            }
            _hover={{
              cursor: 'pointer',
            }}
          >
            {props.isPartial ? <HiOutlineMinus /> : <Checkbox.Indicator />}
          </Checkbox.Control>

          <Checkbox.HiddenInput />
        </Checkbox.Root>

        <AccordionItemTrigger
          style={{ width: '100%' }}
          flex={1}
          px={2}
          backgroundColor="transparent"
          _hover={{
            bg: 'cardHover',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            borderRadius: '0.375rem',
          }}
        >
          <Text as="b" fontSize="10pt" color="secondaryText">
            {props.label}
          </Text>
        </AccordionItemTrigger>
      </Flex>

      <AccordionItemContent
        p={2}
        backgroundColor={props.bg ? props.bg : 'midCardBG'}
        height={props.variant && props.variant === 'compact' ? '150px' : '100%'}
        overflowY={props.variant && props.variant === 'compact' ? 'auto' : 'hidden'}
        scrollbarWidth={'thin'}
      >
        <AccordionRoot multiple={false} width="100%" collapsible boxShadow="none">
          <Stack direction="column" rowGap={props.variant && props.variant === 'compact' ? 0 : 3}>
            {props.children}
          </Stack>
        </AccordionRoot>
      </AccordionItemContent>
    </Accordion.Item>
  );
}

function AccordionCheckboxItem(props: {
  label: string;
  isChecked: boolean;
  onClick: (isChecked: boolean) => void;
}) {
  return (
    <Checkbox.Root
      padding={2}
      _hover={{
        bg: 'cardHover',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
      }}
      checked={props.isChecked}
      onCheckedChange={() => props.onClick(props.isChecked)}
    >
      <Checkbox.Control
        borderColor="primaryText"
        backgroundColor={props.isChecked ? 'primaryText' : 'none'}
      />
      <Checkbox.HiddenInput />
      <Checkbox.Label color="secondaryText">{props.label}</Checkbox.Label>
    </Checkbox.Root>
  );
}

function DataSourceOptions<S extends keyof FilterSource>(props: {
  id: S;
  options: FilterRenderType<S>[];
}) {

  const dispatch = useAppDispatch();

  const anySelected = props.options.some((o) => o.selectedItems.length > 0);
  const allSelected = props.options.every((o) => o.selectedItems.length === Object.keys(o.items).length);

  const isChecked = anySelected; // parent checked if any leaf selected
  const isPartial = anySelected && !allSelected; // indeterminate if mixed

  let func = props.id === 'hudoc' ? setDocumentFilterHUDOC : setDocumentFilterICJ;
  
  // console.log(props.options);

  return (
    <AccordionCheckboxDropdown
      label={Database[props.id].label}
      isChecked={isChecked}
      isPartial={isPartial}
      onClick={() => {
        props.options.forEach((option) => {
          dispatch(func({ type: option.type, value: !allSelected ? Object.keys(option.items) : [] }));
        });
      }}
    >
      {props.options.map((option, i) => {
        
        const selected = option.selectedItems;
        const all = Object.keys(option.items);
        
        const isCheckedAllOptions = selected.length === all.length;
        const isPartialOption = selected.length > 0 && selected.length < all.length;

        return (
          <AccordionCheckboxDropdown
            key={`yeehoo_${i}`}
            label={option.type as string}
            isChecked={isCheckedAllOptions}
            isPartial={isPartialOption}
            variant="compact"
            bg="cardDefault"
            onClick={() => {
              dispatch(func({ type: option.type, value: isCheckedAllOptions ? [] : all }));
            }}
          >
            {Object.keys(option.items).map((item, j) => {
              const isCheckedOption = selected.includes(item);

              return (
                <AccordionCheckboxItem
                  key={`yeehoo_${i}_${j}`}
                  label={option.items[item].label}
                  isChecked={isCheckedOption}
                  onClick={() => {
                    dispatch(
                      func({
                        type: option.type,
                        value: isCheckedOption ? selected.filter((e) => e !== item) : [...selected, item]
                      }),
                    );
                  }}
                />
              );
            })}
          </AccordionCheckboxDropdown>
        );
      })}
    </AccordionCheckboxDropdown>
  );
}

export default function FilterSelector(props: { onClose: Function }) {
  const filter = useAppSelector((state) => state.viewReducer.documents.filter.source);
  const dispatch = useAppDispatch();

  const optionsICJ: FilterRenderType<'icj'>[] = [
    filterOption('icj', 'countries', filter, CountryICJ),
  ];

  const optionsHUDOC: FilterRenderType<'hudoc'>[] = [
    filterOption('hudoc', 'importance', filter, Importance),
    filterOption('hudoc', 'conclusion', filter, Conclusion),
    filterOption('hudoc', 'decisionLevel', filter, DecisionLevel),
    filterOption('hudoc', 'countries', filter, CountryHUDOC),
    filterOption('hudoc', 'articles', filter, Article),
  ];

  return (
    <Card.Root
      variant="elevated"
      backgroundColor="background"
      borderRadius="0.375rem"
      width="360px"
    >
      <Card.Body p={4}>
        <VStack gap={2} alignItems="start">
          <HStack width="100%" justifyContent="space-between">
            <Text color="primaryText" fontWeight={700} fontSize="md">
              Filter
            </Text>
            <CloseButton onClick={() => props.onClose()} />
          </HStack>

          <AccordionRoot multiple={false} width="100%" collapsible boxShadow="none">
            <Stack direction="column" rowGap={3}>
              <Accordion.Item
                borderRadius="0.375rem"
                style={{ boxShadow: '0px 3px 1px -3px #00000080' }}
              >
                <DateRangeSelector />
              </Accordion.Item>

              <DataSourceOptions id="hudoc" options={optionsHUDOC} />

              <DataSourceOptions id="icj" options={optionsICJ} />
            </Stack>
          </AccordionRoot>

          <Flex direction="row" w="full" gap={2}>
            <StyledButton
              variant="outline"
              text="Deselect All"
              fillWidth
              onClick={() => {
                optionsHUDOC.forEach((option) => {
                  dispatch(setDocumentFilterHUDOC({ type: option.type, value: [] }));
                });
                optionsICJ.forEach((option) => {
                  dispatch(setDocumentFilterICJ({ type: option.type, value: [] }));
                });
              }}
            />
            <StyledButton
              variant="solid"
              text="Select All"
              fillWidth
              onClick={() => {
                optionsHUDOC.forEach((option) => {
                  dispatch(
                    setDocumentFilterHUDOC({ type: option.type, value: Object.keys(option.items) }),
                  );
                });
                optionsICJ.forEach((option) => {
                  dispatch(
                    setDocumentFilterICJ({ type: option.type, value: Object.keys(option.items) }),
                  );
                });
                dispatch(
                  setDocumentFilterTime({
                    upper: FilterDefaultOptions.time.upper,
                    lower: FilterDefaultOptions.time.lower,
                  }),
                );
              }}
            />
          </Flex>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
